import React from "react";
import { Plus, Minus, Trash2 } from "lucide-react";
import { imageUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";

/**
 * DishCard — Module 1
 * Mandatory image system: every dish shows either its uploaded image or the
 * clearly-marked placeholder "DISH IMAGE — REPLACE BEFORE LAUNCH".
 * Price is shown ONLY if admin has entered one; otherwise "Price on request".
 * NEVER auto-generate or invent a price/image.
 */
export default function DishCard({ dish, categoryName }) {
  const { t, cart, addToCart, decrementCart, removeFromCart, settings } = useApp();
  const inCart = cart.find((c) => c.id === dish.id);
  const qty = inCart?.qty || 0;
  const currency = settings?.currency_symbol || "₹";

  return (
    <article
      data-testid={`dish-card-${dish.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-border/70 bg-card transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/5"
    >
      {dish.image_id ? (
        <img
          src={imageUrl(dish.image_id)}
          alt={dish.name}
          loading="lazy"
          className="aspect-[4/3] w-full object-cover"
          data-testid={`dish-image-${dish.id}`}
        />
      ) : (
        <div
          className="dish-placeholder"
          data-testid={`dish-placeholder-${dish.id}`}
          role="img"
          aria-label="Dish image placeholder — replace before launch"
        >
          <span className="dish-placeholder-text">DISH IMAGE — REPLACE BEFORE LAUNCH</span>
        </div>
      )}

      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-serif-display text-lg font-semibold text-secondary">
              {dish.name}
            </h3>
            {categoryName && (
              <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">
                {categoryName}
              </p>
            )}
          </div>
          {dish.popular && (
            <span
              data-testid={`dish-popular-${dish.id}`}
              className="rounded-full border border-accent/50 bg-accent/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-secondary"
            >
              {t("menu.popular")}
            </span>
          )}
        </div>

        {dish.description ? (
          <p className="text-sm text-muted-foreground">{dish.description}</p>
        ) : null}

        <div className="mt-auto flex items-end justify-between gap-3 pt-2">
          <div>
            {dish.price !== null && dish.price !== undefined ? (
              <div className="flex items-baseline gap-1" data-testid={`dish-price-${dish.id}`}>
                <span className="tk-wordmark text-xl text-secondary">
                  {currency}
                  {Number(dish.price).toLocaleString("en-IN")}
                </span>
                {dish.pricing_unit && (
                  <span className="text-xs text-muted-foreground">/ {dish.pricing_unit}</span>
                )}
              </div>
            ) : (
              <span
                data-testid={`dish-price-on-request-${dish.id}`}
                className="text-sm font-medium text-secondary/70"
              >
                {t("menu.price_on_request")}
              </span>
            )}
            {!dish.availability && (
              <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-destructive">
                {t("menu.unavailable")}
              </div>
            )}
          </div>

          {dish.availability !== false && (
            <div className="flex items-center gap-2">
              {qty > 0 ? (
                <div
                  className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-background p-1"
                  data-testid={`dish-qty-control-${dish.id}`}
                >
                  <button
                    data-testid={`dish-decrement-${dish.id}`}
                    onClick={() => decrementCart(dish.id)}
                    className="grid h-8 w-8 place-items-center rounded-full text-secondary hover:bg-muted"
                    aria-label="Decrease"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span
                    data-testid={`dish-qty-${dish.id}`}
                    className="min-w-6 text-center text-sm font-semibold"
                  >
                    {qty}
                  </span>
                  <button
                    data-testid={`dish-increment-${dish.id}`}
                    onClick={() => addToCart(dish)}
                    className="grid h-8 w-8 place-items-center rounded-full text-primary hover:bg-primary/10"
                    aria-label="Increase"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`dish-remove-${dish.id}`}
                    onClick={() => removeFromCart(dish.id)}
                    className="grid h-8 w-8 place-items-center rounded-full text-destructive hover:bg-destructive/10"
                    aria-label="Remove"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <button
                  data-testid={`dish-add-${dish.id}`}
                  onClick={() => addToCart(dish)}
                  className="btn-primary !px-5 !py-2 !text-sm"
                >
                  <Plus className="h-4 w-4" /> {t("menu.add")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
